import { useState, useEffect, useMemo, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import API from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { MagnifyingGlass, Book, SquaresFour, Lightning, Briefcase, Folder, ClipboardText, Truck, Buildings, Receipt, Gear, ChartBar, CalendarBlank, ChatCircle, ShieldCheck, FileText, CaretRight, ArrowRight, Info, ThumbsUp, ThumbsDown, Robot, Users, CaretDown, CheckCircle, Warning, XCircle, Plus, PencilSimple } from "@phosphor-icons/react";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import KbArticleEditor from "../components/KbArticleEditor";

/**
 * Phase 287 — Knowledge Base rewrite.
 *
 * Role-aware: admin/PM users see all role tabs; operatives/suppliers/clients
 * are locked to their own track. Search + section filter + 👍/👎 feedback.
 */

const ICON_MAP = {
  SquaresFour, Briefcase, Folder, ClipboardText, Truck, Buildings, Receipt,
  Gear, ChartBar, CalendarBlank, ChatCircle, ShieldCheck, FileText, Lightning,
  Robot, Users, Book,
};

const ROLE_LABELS = {
  admin: { label: "Admin", icon: SquaresFour, blurb: "Full platform — running operations day-to-day." },
  in_house: { label: "In-house Operative", icon: Briefcase, blurb: "Field guide — your jobs, photos, timesheets." },
  vendor: { label: "Supplier", icon: Truck, blurb: "Supplier portal — quotes, jobs, compliance." },
  client: { label: "Client", icon: Buildings, blurb: "Client portal — requests, approvals, invoices." },
};

function getDefaultRoleForUser(user) {
  const r = user?.role || "";
  if (r === "in_house") return "in_house";
  if (r === "vendor") return "vendor";
  if (r === "client") return "client";
  return "admin";
}

function isAdminUser(user) {
  return ["super_admin", "admin", "project_manager"].includes(user?.role || "");
}

export default function KnowledgeBasePage() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [role, setRole] = useState(params.get("role") || getDefaultRoleForUser(user));
  const [query, setQuery] = useState(params.get("q") || "");
  const [sectionData, setSectionData] = useState({ sections: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [openArticle, setOpenArticle] = useState(null);
  const [articleDetail, setArticleDetail] = useState(null);

  const adminUser = isAdminUser(user);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get(`/kb/articles?role=${role}${query ? `&q=${encodeURIComponent(query)}` : ""}`);
      setSectionData(data);
    } catch {
      toast.error("Failed to load Knowledge Base");
    } finally {
      setLoading(false);
    }
  }, [role, query]);

  useEffect(() => { loadList(); }, [loadList]);

  useEffect(() => {
    // Persist current view in the URL so deep-links are shareable
    const sp = new URLSearchParams();
    if (role) sp.set("role", role);
    if (query) sp.set("q", query);
    setParams(sp, { replace: true });
  }, [role, query, setParams]);

  // Open article detail when a card is clicked
  const openArticleDetail = async (slug) => {
    setOpenArticle(slug);
    setArticleDetail(null);
    try {
      const { data } = await API.get(`/kb/articles/${slug}`);
      setArticleDetail(data);
    } catch {
      toast.error("Couldn't load article");
      setOpenArticle(null);
    }
  };

  const totalArticles = sectionData.total || 0;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center">
          <Book size={18} className="text-white" weight="fill" />
        </div>
        <div>
          <h1 className="text-xl sm:text-2xl font-bold font-[Chivo] tracking-tight">Knowledge Base</h1>
          <p className="text-xs text-muted-foreground">{totalArticles} articles · search, browse, or click a topic</p>
        </div>
      </div>

      {/* Search bar */}
      <div className="relative mb-5">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Search articles by title, topic, or tag..."
          className="pl-9 h-10"
          data-testid="kb-search"
        />
      </div>

      {/* Role tabs — admins can switch between tracks */}
      {adminUser ? (
        <Tabs value={role} onValueChange={setRole} className="mb-5">
          <TabsList className="grid grid-cols-4 max-w-2xl">
            {Object.entries(ROLE_LABELS).map(([key, meta]) => {
              const Icon = meta.icon;
              return (
                <TabsTrigger key={key} value={key} data-testid={`kb-role-${key}`} className="text-xs">
                  <Icon size={12} className="mr-1" /> {meta.label}
                </TabsTrigger>
              );
            })}
          </TabsList>
        </Tabs>
      ) : (
        <div className="mb-5 px-3 py-2 border border-border rounded-md bg-muted/40 text-xs flex items-center gap-2" data-testid="kb-role-locked">
          <Info size={12} className="text-primary" />
          Showing articles for: <strong>{ROLE_LABELS[role]?.label || role}</strong> — {ROLE_LABELS[role]?.blurb}
        </div>
      )}

      {/* Phase 287.1 — Knowledge Gaps panel (admin only) */}
      {adminUser && <KnowledgeGapsPanel />}

      {/* Section listing */}
      {loading ? (
        <p className="text-sm text-muted-foreground text-center py-12">Loading articles…</p>
      ) : sectionData.sections.length === 0 ? (
        <div className="text-center py-12" data-testid="kb-empty">
          <Book size={32} className="mx-auto text-muted-foreground/40 mb-2" />
          <p className="text-sm text-muted-foreground">No articles match "{query}".</p>
        </div>
      ) : (
        <div className="space-y-6" data-testid="kb-sections">
          {sectionData.sections.map(section => {
            const Icon = ICON_MAP[section.icon] || Book;
            return (
              <div key={section.id} className="border border-border rounded-md bg-card" data-testid={`kb-section-${section.id}`}>
                <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
                  <Icon size={16} className={section.color || "text-primary"} weight="fill" />
                  <h3 className="text-sm font-semibold font-[Chivo]">{section.title}</h3>
                  <Badge variant="secondary" className="ml-auto text-[10px]">{section.articles.length}</Badge>
                </div>
                <ul className="divide-y divide-border">
                  {section.articles.map(a => (
                    <li key={a.slug} data-testid={`kb-article-${a.slug}`}>
                      <button
                        type="button"
                        onClick={() => openArticleDetail(a.slug)}
                        className="w-full flex items-start gap-3 px-4 py-3 row-hover text-left transition-colors"
                      >
                        <CaretRight size={14} className="text-muted-foreground mt-0.5 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium">{a.title}</p>
                          {a.summary && <p className="text-xs text-muted-foreground mt-0.5">{a.summary}</p>}
                          {a.tags && a.tags.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {a.tags.slice(0, 5).map(t => (
                                <span key={t} className="text-[10px] bg-muted px-1.5 py-0.5 rounded text-muted-foreground">{t}</span>
                              ))}
                            </div>
                          )}
                        </div>
                        {(a.helpful_count || 0) > 0 && (
                          <span className="text-[10px] text-emerald-600 dark:text-emerald-400 shrink-0" title={`${a.helpful_count} found this helpful`}>
                            👍 {a.helpful_count}
                          </span>
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      )}

      {/* Article detail modal */}
      {openArticle && (
        <ArticleModal
          slug={openArticle}
          article={articleDetail}
          onClose={() => { setOpenArticle(null); setArticleDetail(null); }}
        />
      )}
    </div>
  );
}

function ArticleModal({ slug, article, onClose }) {
  const [voted, setVoted] = useState(null); // 'yes' / 'no' / null

  const vote = async (helpful) => {
    if (voted) return;
    setVoted(helpful ? "yes" : "no");
    try {
      await API.post(`/kb/articles/${slug}/feedback`, { helpful });
      toast.success(helpful ? "Thanks for the feedback!" : "Thanks — we'll improve this.");
    } catch {
      toast.error("Couldn't record your feedback");
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="kb-article-modal">
      <div className="bg-card border border-border rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-card border-b border-border px-5 py-3 flex items-center justify-between z-10">
          <div className="flex items-center gap-2 min-w-0">
            <Book size={16} className="text-primary shrink-0" weight="fill" />
            <h2 className="text-sm font-semibold font-[Chivo] truncate">{article?.title || "Loading…"}</h2>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="kb-article-close">Close</Button>
        </div>
        {!article ? (
          <p className="px-5 py-12 text-center text-sm text-muted-foreground">Loading…</p>
        ) : (
          <div className="px-5 py-5">
            {article.summary && <p className="text-sm text-muted-foreground italic mb-4">{article.summary}</p>}
            <article className="prose prose-sm dark:prose-invert max-w-none whitespace-pre-wrap">
              {renderContent(article.content)}
            </article>
            {article.tags && article.tags.length > 0 && (
              <div className="mt-6 flex flex-wrap gap-1.5">
                {article.tags.map(t => (
                  <span key={t} className="text-[10px] bg-muted px-2 py-0.5 rounded-full text-muted-foreground">#{t}</span>
                ))}
              </div>
            )}
            {/* Feedback */}
            <div className="mt-6 pt-4 border-t border-border" data-testid="kb-article-feedback">
              <p className="text-xs text-muted-foreground mb-2">Was this article helpful?</p>
              <div className="flex gap-2">
                <Button
                  variant={voted === "yes" ? "default" : "outline"}
                  size="sm"
                  onClick={() => vote(true)}
                  disabled={!!voted}
                  data-testid="kb-feedback-yes"
                >
                  <ThumbsUp size={14} className="mr-1" /> Yes {article.helpful_count > 0 ? `(${article.helpful_count})` : ""}
                </Button>
                <Button
                  variant={voted === "no" ? "destructive" : "outline"}
                  size="sm"
                  onClick={() => vote(false)}
                  disabled={!!voted}
                  data-testid="kb-feedback-no"
                >
                  <ThumbsDown size={14} className="mr-1" /> No {article.not_helpful_count > 0 ? `(${article.not_helpful_count})` : ""}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Minimal markdown-ish render — supports **bold**, line breaks, and bullet lines
function renderContent(content) {
  if (!content) return null;
  const lines = content.split("\n");
  return lines.map((line, i) => {
    if (!line.trim()) return <br key={i} />;
    // Bold inline replacement
    const segments = [];
    let last = 0;
    const re = /\*\*(.+?)\*\*/g;
    let m;
    while ((m = re.exec(line)) !== null) {
      if (m.index > last) segments.push(line.slice(last, m.index));
      segments.push(<strong key={`b${i}-${m.index}`}>{m[1]}</strong>);
      last = m.index + m[0].length;
    }
    if (last < line.length) segments.push(line.slice(last));
    return <p key={i} className="text-sm leading-relaxed mb-2">{segments.length ? segments : line}</p>;
  });
}


// ─────────────────────────────────────────────────────────────
// Phase 287.1 — Knowledge Gaps panel (admin-only)
// Lists questions where the AI answer was rated unhelpful, so admins
// know what new articles to write.
// ─────────────────────────────────────────────────────────────
function KnowledgeGapsPanel() {
  const { user } = useAuth();
  const canFixSeeded = user?.role === "super_admin";
  const [items, setItems] = useState([]);
  const [openTotal, setOpenTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState("open");
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [openGap, setOpenGap] = useState(null);
  const [editor, setEditor] = useState(null); // {mode, slug?, gap}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get(`/kb/gap-reports?status=${statusFilter}&limit=50`);
      setItems(data.items || []);
      setOpenTotal(data.open_total || 0);
    } catch {
      // silent — admin tool
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const resolveGap = async (gap, articleSlug = "", note = "") => {
    try {
      await API.post(`/kb/gap-reports/${gap.id}/resolve`, { note, article_slug: articleSlug });
      toast.success("Marked as resolved");
      setOpenGap(null);
      load();
    } catch {
      toast.error("Couldn't resolve gap");
    }
  };

  const reopenGap = async (gap) => {
    try {
      await API.post(`/kb/gap-reports/${gap.id}/reopen`, {});
      toast.success("Reopened");
      load();
    } catch {
      toast.error("Couldn't reopen");
    }
  };

  // Don't render the panel at all if no open gaps and the section is collapsed
  if (!expanded && openTotal === 0 && !loading) return null;

  return (
    <div className="mb-6 border border-border rounded-md bg-card" data-testid="kb-gaps-panel">
      <button
        type="button"
        onClick={() => setExpanded(v => !v)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-muted/30 transition-colors"
        data-testid="kb-gaps-toggle"
      >
        <div className="flex items-center gap-2">
          <Warning size={16} className="text-amber-600 dark:text-amber-400" weight="fill" />
          <span className="text-sm font-semibold font-[Chivo]">Unanswered Questions</span>
          <span className="text-[11px] text-muted-foreground hidden sm:inline">— Ask AI answers that got a thumbs-down</span>
          {openTotal > 0 && (
            <span className="text-[10px] bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 px-1.5 py-0.5 rounded-full font-semibold">{openTotal} open</span>
          )}
        </div>
        <CaretDown size={14} className={`text-muted-foreground transition-transform ${expanded ? "rotate-180" : ""}`} />
      </button>
      {expanded && (
        <div className="border-t border-border" data-testid="kb-gaps-list">
          <div className="px-4 py-2 border-b border-border flex items-center gap-2 bg-muted/20">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Status:</span>
            {["open", "resolved", "all"].map(s => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`text-[10px] px-2 py-0.5 rounded ${statusFilter === s ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-muted-foreground/10"}`}
                data-testid={`kb-gaps-filter-${s}`}
              >
                {s}
              </button>
            ))}
          </div>
          {loading ? (
            <p className="text-center text-xs text-muted-foreground py-6">Loading…</p>
          ) : items.length === 0 ? (
            <p className="text-center text-xs text-muted-foreground py-6">No {statusFilter !== "all" ? statusFilter : ""} unanswered questions — your guides are keeping up nicely.</p>
          ) : (
            <ul className="divide-y divide-border">
              {items.map(g => (
                <li key={g.id} className="px-4 py-3" data-testid={`kb-gap-${g.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium leading-snug">{g.question}</p>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <span className="text-[10px] text-muted-foreground">{g.hit_count} hit{g.hit_count !== 1 ? "s" : ""}</span>
                        <span className="text-[10px] text-muted-foreground">·</span>
                        <span className="text-[10px] text-muted-foreground capitalize">{g.user_role}</span>
                        <span className="text-[10px] text-muted-foreground">·</span>
                        <span className="text-[10px] text-muted-foreground">{g.last_seen_at ? new Date(g.last_seen_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : ""}</span>
                        {g.status === "resolved" && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 flex items-center gap-1">
                            <CheckCircle size={9} /> resolved
                          </span>
                        )}
                      </div>
                      {g.comment && (
                        <p className="text-[11px] text-muted-foreground italic mt-1">User note: "{g.comment}"</p>
                      )}
                      {g.status === "open" && (
                        <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                          <Button size="sm" className="h-6 text-[11px]" onClick={() => setEditor({ mode: "create", gap: g })} data-testid={`kb-gap-add-${g.id}`}>
                            <Plus size={11} className="mr-1" /> Add guide
                          </Button>
                          {canFixSeeded && (g.citations || []).map(c => (
                            <Button key={c.slug} size="sm" variant="outline" className="h-6 text-[11px]" onClick={() => setEditor({ mode: "edit", slug: c.slug, gap: g })} data-testid={`kb-gap-fix-${g.id}-${c.slug}`}>
                              <PencilSimple size={11} className="mr-1" /> Fix "{(c.title || c.slug).slice(0, 34)}{(c.title || c.slug).length > 34 ? "…" : ""}"
                            </Button>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        type="button"
                        onClick={() => setOpenGap(g)}
                        className="text-[11px] text-primary hover:underline"
                        data-testid={`kb-gap-view-${g.id}`}
                      >
                        View
                      </button>
                      {g.status === "open" ? (
                        <button
                          type="button"
                          onClick={() => resolveGap(g)}
                          className="text-[11px] text-emerald-600 dark:text-emerald-400 hover:underline ml-2"
                          data-testid={`kb-gap-resolve-${g.id}`}
                        >
                          Resolve
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => reopenGap(g)}
                          className="text-[11px] text-muted-foreground hover:text-foreground hover:underline ml-2"
                          data-testid={`kb-gap-reopen-${g.id}`}
                        >
                          Reopen
                        </button>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Gap detail modal */}
      {openGap && (
        <GapDetailModal
          gap={openGap}
          onClose={() => setOpenGap(null)}
          onResolve={(slug, note) => resolveGap(openGap, slug, note)}
          onAddGuide={() => { setEditor({ mode: "create", gap: openGap }); setOpenGap(null); }}
          onFixGuide={canFixSeeded ? (slug) => { setEditor({ mode: "edit", slug, gap: openGap }); setOpenGap(null); } : null}
        />
      )}
      {editor && (
        <KbArticleEditor
          mode={editor.mode}
          slug={editor.slug}
          gap={editor.gap}
          onClose={() => setEditor(null)}
          onSaved={() => load()}
        />
      )}
    </div>
  );
}

function GapDetailModal({ gap, onClose, onResolve, onAddGuide, onFixGuide }) {
  const [articleSlug, setArticleSlug] = useState(gap.linked_article_slug || "");
  const [note, setNote] = useState(gap.resolution_note || "");
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="kb-gap-modal">
      <div className="bg-card border border-border rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-border flex items-center justify-between">
          <h3 className="text-sm font-semibold font-[Chivo]">Knowledge Gap</h3>
          <button onClick={onClose} className="text-xs text-muted-foreground hover:text-foreground" data-testid="kb-gap-modal-close">Close</button>
        </div>
        <div className="px-5 py-4 space-y-3">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Question</p>
            <p className="text-sm mt-0.5">{gap.question}</p>
          </div>
          <div className="flex gap-4 text-[11px] text-muted-foreground">
            <span><strong className="text-foreground">{gap.hit_count}</strong> hit{gap.hit_count !== 1 ? "s" : ""}</span>
            <span className="capitalize"><strong className="text-foreground">{gap.user_role}</strong> role</span>
            <span>First seen <strong className="text-foreground">{gap.first_seen_at ? new Date(gap.first_seen_at).toLocaleDateString("en-GB") : ""}</strong></span>
          </div>
          {gap.sample_answer && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">AI answer (sample)</p>
              <p className="text-xs mt-0.5 p-2 bg-muted/30 rounded whitespace-pre-wrap">{gap.sample_answer}</p>
            </div>
          )}
          {gap.occurrences && gap.occurrences.length > 1 && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Recent occurrences ({gap.occurrences.length})</p>
              <ul className="space-y-1 max-h-48 overflow-y-auto">
                {gap.occurrences.slice(-10).reverse().map((o, i) => (
                  <li key={i} className="text-[11px] p-2 bg-muted/20 rounded">
                    <p className="text-foreground">{o.question}</p>
                    {o.comment && <p className="italic text-muted-foreground mt-0.5">"{o.comment}"</p>}
                    <p className="text-[9px] text-muted-foreground mt-0.5">{o.user_role} · {o.created_at ? new Date(o.created_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : ""}</p>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {(gap.citations || []).length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Guides the AI used</p>
              <div className="flex flex-wrap gap-1.5">
                {gap.citations.map(c => (
                  <span key={c.slug} className="text-[11px] px-2 py-0.5 rounded-full bg-muted border border-border">{c.title || c.slug}</span>
                ))}
              </div>
            </div>
          )}
          {gap.status === "open" && (
            <div className="flex flex-wrap gap-1.5" data-testid="kb-gap-modal-actions">
              <Button size="sm" className="h-7 text-xs" onClick={onAddGuide} data-testid="kb-gap-modal-add"><Plus size={12} className="mr-1" /> Add a guide for this</Button>
              {onFixGuide && (gap.citations || []).map(c => (
                <Button key={c.slug} size="sm" variant="outline" className="h-7 text-xs" onClick={() => onFixGuide(c.slug)} data-testid={`kb-gap-modal-fix-${c.slug}`}>
                  <PencilSimple size={12} className="mr-1" /> Fix "{(c.title || c.slug).slice(0, 30)}{(c.title || c.slug).length > 30 ? "…" : ""}"
                </Button>
              ))}
            </div>
          )}
          {gap.status === "open" && (
            <div className="pt-3 border-t border-border space-y-2">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Resolve</p>
              <Input
                value={articleSlug}
                onChange={e => setArticleSlug(e.target.value)}
                placeholder="Linked article slug (optional, e.g. 'create-a-job')"
                className="h-8 text-xs"
                data-testid="kb-gap-resolve-slug"
              />
              <textarea
                value={note}
                onChange={e => setNote(e.target.value)}
                placeholder="Resolution note (optional)"
                rows={2}
                className="w-full text-xs rounded border border-border bg-background px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-primary"
                data-testid="kb-gap-resolve-note"
              />
              <Button size="sm" onClick={() => onResolve(articleSlug, note)} data-testid="kb-gap-modal-resolve">
                <CheckCircle size={12} className="mr-1" /> Mark resolved
              </Button>
            </div>
          )}
          {gap.status === "resolved" && (
            <div className="pt-3 border-t border-border text-[11px]">
              <p className="text-emerald-600 dark:text-emerald-400 font-medium flex items-center gap-1">
                <CheckCircle size={11} weight="fill" /> Resolved by {gap.resolved_by_name || "admin"}
                {gap.resolved_at && ` on ${new Date(gap.resolved_at).toLocaleDateString("en-GB")}`}
              </p>
              {gap.resolution_note && <p className="text-muted-foreground italic mt-1">"{gap.resolution_note}"</p>}
              {gap.linked_article_slug && (
                <p className="text-muted-foreground mt-1">Linked article: <code className="text-foreground">{gap.linked_article_slug}</code></p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
